import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { UncoveredSlotRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Substitutions.
 * Pick today's absent teachers → the page shows every slot they were meant to
 * teach + the "free teachers" picker for each. Assign a proxy → substitution
 * created; the slot flips to "covered".
 */
const DAY_LABELS = ['—', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function SubstitutionsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [absent, setAbsent] = useState<string[]>([]);
  const [assignFor, setAssignFor] = useState<UncoveredSlotRow | null>(null);

  const staff = useQuery({
    queryKey: ['staff', institution],
    queryFn: () => productApi.listStaff(institution),
    enabled: !!institution,
  });

  const summary = useQuery({
    queryKey: ['substitutions', institution, date, absent.join(',')],
    queryFn: () => productApi.getSubstitutions(institution, date, absent),
    enabled: !!institution && absent.length > 0,
  });

  const rows = summary.data?.slots ?? [];
  const covered = rows.filter((r) => r.substitution_id).length;
  const uncovered = rows.length - covered;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Substitutions</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Pick today's absent teachers. Every slot they had is listed with a "free teachers" picker.
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          placeholder="yyyy-mm-dd"
          style={{ width: 140, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        />
        <Select
          size="small"
          mode="multiple"
          value={absent}
          onChange={setAbsent}
          placeholder="Absent teachers (pick one or more)"
          style={{ minWidth: 360 }}
          allowClear
          options={(staff.data?.rows ?? [])
            .filter((s) => s.status === 'active')
            .map((s) => ({ value: s.id, label: `${s.name} · ${s.role}` }))}
        />
      </div>

      {absent.length > 0 && summary.data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          <Card size="small"><Typography.Text type="secondary" style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>Slots today</Typography.Text><div style={{ fontSize: 20, fontWeight: 600 }}>{rows.length}</div></Card>
          <Card size="small"><Typography.Text type="secondary" style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>Covered</Typography.Text><div style={{ fontSize: 20, fontWeight: 600, color: '#1c6442' }}>{covered}</div></Card>
          <Card size="small"><Typography.Text type="secondary" style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>Uncovered</Typography.Text><div style={{ fontSize: 20, fontWeight: 600, color: uncovered > 0 ? '#b3382f' : '#26304a' }}>{uncovered}</div></Card>
        </div>
      )}

      <Card size="small" title={absent.length > 0 ? `Slots on ${date}` : 'Pick absent teachers to begin'}>
        {absent.length === 0 && <Empty description="Nothing selected yet." style={{ padding: 20 }} />}
        {summary.isLoading && <Skeleton active paragraph={{ rows: 5 }} />}
        {summary.data && absent.length > 0 && rows.length === 0 && (
          <Empty description="The absent teacher(s) have no scheduled slots today. Nothing to cover." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<UncoveredSlotRow>
            size="small"
            pagination={false}
            rowKey="slot_id"
            dataSource={rows}
            columns={[
              { title: 'Section', key: 'sec', render: (_, r) => <b>{r.grade}{r.section ? `-${r.section}` : ''}</b> },
              { title: 'Day', dataIndex: 'day_of_week', key: 'd', width: 60, render: (v: number) => DAY_LABELS[v] ?? v },
              { title: 'Period', dataIndex: 'period', key: 'p', width: 70, render: (v: number) => `P${v}` },
              { title: 'Subject', dataIndex: 'subject_name', key: 'sub' },
              { title: 'Was to teach', dataIndex: 'original_teacher_name', key: 'orig', render: (v: string | null) => v ?? <Typography.Text type="secondary">unassigned</Typography.Text> },
              {
                title: 'Cover',
                key: 'cover',
                width: 240,
                render: (_, r) =>
                  r.substitution_id ? (
                    <Tag color="success">
                      Covered by {r.covered_by_name}
                    </Tag>
                  ) : (
                    <Button size="small" type="primary" onClick={() => setAssignFor(r)}>Assign proxy</Button>
                  ),
              },
            ]}
          />
        )}
      </Card>

      <AssignProxyModal
        row={assignFor}
        date={date}
        institution={institution}
        onClose={() => setAssignFor(null)}
        onDone={() => {
          setAssignFor(null);
          void qc.invalidateQueries({ queryKey: ['substitutions', institution] });
          void message.success('Proxy assigned');
        }}
      />
    </Space>
  );
}

function AssignProxyModal({
  row, date, institution, onClose, onDone,
}: {
  row: UncoveredSlotRow | null;
  date: string;
  institution: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const free = useQuery({
    queryKey: ['free-teachers', institution, row?.day_of_week, row?.period],
    queryFn: () => productApi.getFreeTeachers(institution, row!.day_of_week, row!.period),
    enabled: !!row,
  });
  const [proxy, setProxy] = useState<string | undefined>();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () =>
      productApi.createSubstitution(institution, {
        date,
        slot_id: row!.slot_id,
        proxy_teacher_id: proxy!,
        reason: reason || undefined,
      }),
    onSuccess: () => { setProxy(undefined); setReason(''); onDone(); },
  });
  const body = m.error instanceof ApiError ? (m.error.body as { message?: string } | null) : null;
  const err = body?.message ?? (m.error instanceof Error ? m.error.message : null);

  return (
    <Modal
      open={!!row}
      title="Assign proxy teacher"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Assign"
      confirmLoading={m.isPending}
      onOk={() => proxy && m.mutate()}
      okButtonProps={{ disabled: !proxy }}
      destroyOnClose
    >
      {row && (
        <>
          <div style={{ marginBottom: 12, padding: 10, background: '#f7f7f5', borderRadius: 6 }}>
            <div style={{ fontWeight: 600 }}>
              {row.grade}{row.section ? `-${row.section}` : ''} · {row.subject_name} · P{row.period}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
              was to be taught by {row.original_teacher_name ?? 'unassigned'}
            </Typography.Text>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 4 }}>Free at this period</div>
            <Select
              value={proxy}
              onChange={setProxy}
              placeholder="Pick a free teacher"
              style={{ width: '100%' }}
              options={(free.data?.free ?? []).map((t) => ({ value: t.id, label: `${t.name} · ${t.role}` }))}
              notFoundContent={free.isLoading ? 'Loading…' : 'No teacher is free'}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 4 }}>Reason (optional)</div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. sick leave" />
          </div>
          {err && <Alert type="error" showIcon message={err} />}
        </>
      )}
    </Modal>
  );
}
